import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import InviteBranchModal from './InviteBranchModal'
import ViewEditBranches from './ViewEditBranches'
import FiltersModal, { FilterType } from '../common/FiltersModal'
import SearchWithFilter from '../common/SearchWithFilter'
import EmptyPageResults from '../common/EmptyPageResults'
import LoadingSpinner from '../common/LoadingSpinner'
import Dropdown from '../common/Dropdown'
import SimpleButton from '../common/SimpleButton'
import GenericTable from '../common/GenericTable'
import ActionsMenu from '../common/ActionsMenu'
import ExpandableText from '../common/ExpandableText'
import ConfirmationModal from '../common/ConfirmationModal'
import { toast } from '../common/Toast'
import { MenuKebabVerticalIcon, MenuKebabVerticalBackgroundIcon } from '../../icons'
import { branchesWritePermission, companiesWritePermission } from '../../utils/permissionValidation'
import { drAuthRules } from '../../utils/drConfig'
import { useWindowSize } from '../../libs/hooks'
import { useDeepCompareEffect } from '../../hooks/useDeepCompareEffect'
import {
  clearCorporateBranchMessages,
  loadCorporateBranchById,
  loadCorporateBranches,
  loadCorporateBranchCities,
  loadCorporateBranchStateProvinces,
  removeBranchFromCorporation,
} from '../../actions/corporate'

const orderingFields = [
  {
    field: 'branch',
    order: 'asc',
    translationKey: 'branch_asc',
  },
  {
    field: 'branch',
    order: 'desc',
    translationKey: 'branch_desc',
  },
  {
    field: 'email',
    order: 'asc',
    translationKey: 'admin_email_asc',
  },
  {
    field: 'email',
    order: 'desc',
    translationKey: 'admin_email_desc',
  },
  {
    field: 'officeAddressOne',
    order: 'asc',
    translationKey: 'address_asc',
  },
  {
    field: 'officeAddressOne',
    order: 'desc',
    translationKey: 'address_desc',
  },
  {
    field: 'members',
    order: 'asc',
    translationKey: 'members_asc',
  },
  {
    field: 'members',
    order: 'desc',
    translationKey: 'members_desc',
  },
  {
    field: 'devices',
    order: 'asc',
    translationKey: 'devices_asc',
  },
  {
    field: 'devices',
    order: 'desc',
    translationKey: 'devices_desc',
  },
]

const Branches = (props) => {
  const { t } = useTranslation()
  const isMobile = useWindowSize()[0] <= 768
  const [branchesTableData, setBranchesTableData] = useState([])
  const [shouldApplyFilters, setShouldApplyFilters] = useState(true)
  const [cityFilter, setCityFilter] = useState([])
  const [stateProvinceFilter, setStateProvinceFilter] = useState([])
  const [searchParam, setSearchParam] = useState(null)
  const [orderBy, setOrderBy] = useState(null)
  const [showSpinner, setShowSpinner] = useState(null)
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [showActionsMenu, setShowActionsMenu] = useState(null)
  const [canLoadMore, setCanLoadMore] = useState(false)
  const [selectedPage, setSelectedPage] = useState(1)
  const [isFirstLoad, setIsFirstLoad] = useState(true)
  const [showViewEditBranchModal, setShowViewEditBranchModal] = useState({ isOpen: false, isEdit: false })
  const [showInviteBranchModal, setShowInviteBranchModal] = useState(false)
  const [expandedBranch, setExpandedBranch] = useState(0)
  const [selectedBranch, setSelectedBranch] = useState(null)
  const [branchToDelete, setBranchToDelete] = useState(null)
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
  const companyId = props.companyId

  const buildAddress = (branch) => {
    let address = ''

    if (branch.officeAddressOne) {
      address += `${branch.officeAddressOne} `
    }

    if (branch.officeAddressTwo) {
      address += `${branch.officeAddressTwo} `
    }

    if (branch.city) {
      address += `${branch.city} `
    }

    if (branch.stateProvinceRegion) {
      address += `${branch.stateProvinceRegion} `
    }

    if (branch.zipPostalCode) {
      address += `${branch.zipPostalCode} `
    }

    if (branch.country) {
      address += `${branch.country}`
    }

    return address.trimEnd()
  }

  useEffect(() => {
    const tableData = props.branches.map((branch, index) => {
      return {
        index,
        identifier: branch.id,
        branchName: branch.branchName,
        email: branch.email,
        address: buildAddress(branch),
        devices: branch.totalDevices,
        members: branch.members,
        actions: [
          {
            label: 'view',
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEditBranch(branch.id, false)
            },
          },
          {
            label: 'edit',
            isDisabled: !drAuthRules.isCompanyAccountUpdateAllowed,
            onActionClick: (e) => {
              e.stopPropagation()
              handleOpenViewEditBranch(branch.id, true)
            },
            requiredPermission: companiesWritePermission,
          },
          {
            label: 'remove',
            isDisabled: !drAuthRules.isCompanyAccountUpdateAllowed,
            onActionClick: (e) => {
              e.stopPropagation()
              showDeleteConfirmationModal(branch)
            },
            requiredPermission: companiesWritePermission,
          },
        ],
      }
    })

    setBranchesTableData(tableData)
  }, [props.branches])

  useEffect(() => {
    if (!companyId) {
      return
    }

    if (props.cities.length === 0) {
      props.actions.loadCorporateBranchCities(companyId)
    }

    if (props.stateProvinces.length === 0) {
      props.actions.loadCorporateBranchStateProvinces(companyId)
    }
  }, [props.companyId])

  useEffect(() => {
    setCanLoadMore(props.canLoadMore)
  }, [props.canLoadMore])

  useEffect(() => {
    setShowSpinner(props.isLoading)
  }, [props.isLoading])

  useEffect(() => {
    if (props.errorMessage) {
      toast.error(props.errorMessage)

      props.actions.clearCorporateBranchMessages()
    }
  }, [props.errorMessage])

  useEffect(() => {
    if (props.successMessage) {
      toast.success(props.successMessage)

      props.actions.clearCorporateBranchMessages()
    }
  }, [props.successMessage])

  useEffect(() => {
    if (selectedPage && selectedPage > 1) {
      handleLoadBranches(false)
    }
  }, [selectedPage])

  useDeepCompareEffect(() => {
    let changeValueTimeout = window.setTimeout(
      () => {
        if (shouldApplyFilters) {
          setSelectedPage(1)
          handleLoadBranches(true)
        }

        setShouldApplyFilters(true)
      },
      searchParam ? 1000 : 0,
    )
    return () => {
      clearTimeout(changeValueTimeout)
    }
  }, [searchParam, cityFilter, stateProvinceFilter, orderBy])

  const showDeleteConfirmationModal = (branch) => {
    setBranchToDelete(branch)
    setShowDeleteConfirmModal(true)
  }

  const handleLoadBranches = (withReset) => {
    if (!companyId) return

    let queryParams = createQueryParams()

    queryParams = {
      ...queryParams,
      offset: withReset ? 0 : (selectedPage - 1) * process.env.REACT_APP_PAGINATION_SIZE,
      limit: process.env.REACT_APP_PAGINATION_SIZE,
    }

    props.actions.loadCorporateBranches(companyId, queryParams, withReset).then(() => {
      setIsFirstLoad(false)
    })
  }

  const handleSearch = (value) => {
    if (value?.length > 2) {
      setShowSpinner(true)
      setSearchParam(value)
    } else if (value?.length === 0 && searchParam?.length > 0) {
      setShowSpinner(true)
      setSearchParam(value)
    }
  }

  const handleSort = (orderingField) => {
    setOrderBy(orderingFields.find((o) => o.field === orderingField.field && o.order === orderingField.order))
  }

  const handleLoadMore = () => {
    setSelectedPage((prevValue) => (prevValue ? prevValue + 1 : 2))
  }

  const handleResetFilters = () => {
    setCityFilter([])
    setStateProvinceFilter([])
  }

  const createQueryParams = () => {
    let queryParams = {}

    if (searchParam) {
      queryParams = { ...queryParams, query: searchParam }
    }

    if (cityFilter.length > 0) {
      queryParams = { ...queryParams, city: cityFilter }
    }

    if (stateProvinceFilter.length > 0) {
      queryParams = { ...queryParams, stateProvinceRegion: stateProvinceFilter }
    }

    if (orderBy?.field && orderBy?.order) {
      queryParams = { ...queryParams, orderBy: orderBy.field, order: orderBy.order }
    }

    return queryParams
  }

  const selectOrderByOptions = (orderByTranslationKey) => {
    const sortOption = orderingFields.find((field) => field.translationKey === orderByTranslationKey)
    handleSort(sortOption)
  }

  const handleOpenViewEditBranch = async (branchId, isEditEnabled = false) => {
    try {
      setShowSpinner(true)

      const response = await props.actions.loadCorporateBranchById(companyId, branchId)
      const branch = response?.value

      if (branch) {
        setSelectedBranch(branch)
        setShowViewEditBranchModal({ isOpen: true, isEdit: isEditEnabled })
      }
    } catch (e) {
      toast.error(t('organization_context.failed_to_load_branch'))
    } finally {
      setShowSpinner(false)
    }
  }

  const closeAddEditModal = () => {
    setShowViewEditBranchModal({ isOpen: false, isEdit: false })
    setSelectedBranch(null)
  }

  const toggleShowActionsMenu = (index) => {
    setShowActionsMenu(showActionsMenu === index ? null : index)
  }

  return (
    <Fragment>
      <div className="branches-wrapper">
        <div className="actions-wrapper">
          <SearchWithFilter
            onSearch={(e) => handleSearch(e)}
            showFiltersModal={() => setShowFiltersModal(true)}
            onResetFilters={() => handleResetFilters()}
            showCounterBadge={cityFilter.length > 0 || stateProvinceFilter.length > 0}
            counterBadge={cityFilter.length + stateProvinceFilter.length}
          />
          <SimpleButton className="submit-button" onClick={() => setShowInviteBranchModal(true)} disabled={!drAuthRules.isCompanyAccountUpdateAllowed}>
            {t('organization_context.add_branch')}
          </SimpleButton>
        </div>
        {isMobile && branchesTableData.length > 0 && (
          <div className="order-by-mobile-wrapper">
            <Dropdown
              className="dropdown-input"
              placeholder={t('select_ordering')}
              options={orderingFields.map((field) => ({ key: field.translationKey, value: t(`sortable_context.${field.translationKey}`) }))}
              initialSelectedKey={orderBy?.translationKey}
              onChange={(orderBy) => selectOrderByOptions(orderBy.key)}
            />
          </div>
        )}
        {!isMobile && branchesTableData.length > 0 && (
          <GenericTable
            data={branchesTableData}
            headers={[
              {
                title: t('branch'),
                orderKey: 'branch',
              },
              {
                title: t('organization_context.admin_email'),
                orderKey: 'email',
              },
              {
                title: t('address'),
                orderKey: 'officeAddressOne',
              },
              {
                title: t('devices'),
                orderKey: 'devices',
              },
              {
                title: t('nav.members'),
                orderKey: 'members',
              },
              {
                title: '',
              },
            ]}
            keys={['branchName', 'email', 'address', 'devices', 'members', 'action']}
            keyRenderer={{
              address: (item) => (
                <div className="address-table-wrapper">
                  <ExpandableText text={item.address} />
                </div>
              ),
              action: (item) => (
                <div
                  className="table-actions-wrapper"
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleShowActionsMenu(item.index)
                  }}
                >
                  {showActionsMenu === item.index ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                  {showActionsMenu === item.index && (
                    <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={item.actions} isLoading={showSpinner} />
                  )}
                </div>
              ),
            }}
            onRowClick={(branch) => handleOpenViewEditBranch(branch.identifier)}
            activeSort={orderBy}
            onSort={(orderingField) => handleSort(orderingField)}
            isLoading={showSpinner}
          />
        )}
        {isMobile &&
          branchesTableData.map((branch, index) => (
            <div key={index} className="card" onClick={() => setExpandedBranch(index)}>
              <div className={'card-item' + (expandedBranch !== index ? ' align-center' : '')}>
                <div className="card-item-title">{t('branch')}</div>
                <div className={'card-item-body' + (expandedBranch !== index ? ' align-center' : '')}>
                  <div>{branch.branchName}</div>
                  <div
                    className="card-actions"
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowActionsMenu(index)
                    }}
                  >
                    {showActionsMenu === index ? <MenuKebabVerticalBackgroundIcon /> : <MenuKebabVerticalIcon />}
                    {showActionsMenu === index && <ActionsMenu setHideMenu={() => setShowActionsMenu(null)} actions={branch.actions} isLoading={showSpinner} />}
                  </div>
                </div>
              </div>
              {expandedBranch === index && (
                <>
                  <div className="card-item">
                    <div className="card-item-title">{t('organization_context.admin_email')}</div>
                    <div className="card-item-body email-wrapper">{branch.email}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('address')}</div>
                    <div className="card-item-body">{branch.address}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('devices')}</div>
                    <div className="card-item-body">{branch.devices}</div>
                  </div>
                  <div className="card-item">
                    <div className="card-item-title">{t('nav.members')}</div>
                    <div className="card-item-body">{branch.members}</div>
                  </div>
                </>
              )}
            </div>
          ))}

        {!showSpinner && !isFirstLoad && branchesTableData.length === 0 && (
          <EmptyPageResults title={t('organization_context.no-branches-found')} subtitle={t('cannot_find_matching_search')} />
        )}
        {canLoadMore && (
          <div className="buttons-wrapper">
            <SimpleButton className="load-more-button" onClick={() => handleLoadMore()}>
              {t('load_more')}
            </SimpleButton>
          </div>
        )}
      </div>
      {showFiltersModal && (
        <FiltersModal
          filters={[
            {
              name: t('city'),
              type: FilterType.multiSelect,
              dataset: props.cities,
              input: cityFilter,
              output: (filterCities) => {
                setCityFilter(filterCities)
              },
            },
            {
              name: t('state_province'),
              type: FilterType.multiSelect,
              dataset: props.stateProvinces,
              input: stateProvinceFilter,
              output: (filterStateProvince) => {
                setStateProvinceFilter(filterStateProvince)
              },
            },
          ]}
          shouldFilter={(shouldApply) => setShouldApplyFilters(shouldApply)}
          resetFilters={() => handleResetFilters()}
          closeFilters={() => setShowFiltersModal(false)}
        />
      )}
      {showSpinner && (
        <div className={'spinner-wrapper'}>
          <LoadingSpinner />
        </div>
      )}
      {showDeleteConfirmModal && (
        <ConfirmationModal
          onToggle={() => {
            setShowDeleteConfirmModal(false)
            setBranchToDelete(null)
          }}
          onAccept={async () => {
            if (branchToDelete?.id) {
              await props.actions.removeBranchFromCorporation(companyId, branchToDelete.id)
            }
            setShowDeleteConfirmModal(false)
            setBranchToDelete(null)
          }}
          onCancel={() => {
            setShowDeleteConfirmModal(false)
            setBranchToDelete(null)
          }}
          message={t('organization_context.confirm_remove_from_corporation')}
          acceptButtonText={t('remove')}
          isWarning
          requiredPermission={branchesWritePermission}
        >
          <div className="remove-branch-sublabel">
            <p>{t('organization_context.confirm_remove_from_corporation_extra')}</p>
          </div>
        </ConfirmationModal>
      )}
      {showInviteBranchModal && <InviteBranchModal closeModal={() => setShowInviteBranchModal(false)} companyId={companyId} />}
      {showViewEditBranchModal.isOpen && (
        <ViewEditBranches
          isEditMode={showViewEditBranchModal.isEdit}
          selectedBranch={selectedBranch}
          closeModal={closeAddEditModal}
          showSpinner={showSpinner}
          companyId={companyId}
        />
      )}
    </Fragment>
  )
}

function stateToProps({ corporate, userCompany }) {
  return {
    branches: corporate?.branches || [],
    companyId: userCompany?.companyId,
    isLoading: corporate?.isLoading,
    canLoadMore: corporate?.canLoadMore,
    errorMessage: corporate?.errorMessage,
    successMessage: corporate?.successMessage,
    branchDetail: corporate?.branchDetail,
    cities: corporate?.branchCities || [],
    stateProvinces: corporate?.branchStateProvinces || [],
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        loadCorporateBranches,
        loadCorporateBranchById,
        loadCorporateBranchCities,
        loadCorporateBranchStateProvinces,
        removeBranchFromCorporation,
        clearCorporateBranchMessages,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(Branches)
